// L'ANTEPRIMA, GENERATA DAL BROWSER (2026-09-19).
//
// PERCHE' QUI E NON SUL SERVER. Il server non ha la chiave e non puo' aprire
// il file: se potesse, la promessa zero-knowledge sarebbe finita. Il browser
// invece il file ce l'ha in chiaro nel momento esatto in cui lo sta per
// cifrare — e' lo stesso oggetto File da cui legge i blocchi — quindi la
// riduzione costa una lettura che sta gia' facendo.
//
// LA REGOLA CHE NON DEVE SALTARE: questa funzione non lancia MAI. Se
// l'immagine e' corrotta, se e' un formato che il browser non sa disegnare
// (HEIC su desktop), se e' enorme, si restituisce null e il file si carica
// lo stesso. Un accessorio non rompe la funzione principale.
import { encryptBuffer } from './crypto';

export const ANTEPRIMA_LATO = 256;
export const ANTEPRIMA_VERSIONE = 1;

// Solo immagini in questo passo: niente video, niente PDF. L'elenco e' lo
// stesso dei tipi per cui la griglia prova gia' a disegnare una miniatura
// (components/ImageThumb.jsx), piu' heic/heif: li' il browser potrebbe
// saperli aprire o no, e se non li sa aprire cade nel ramo del null.
const TIPI = /^image\/(jpeg|png|webp|gif|heic|heif)$/i;
const ESTENSIONI = /\.(jpe?g|png|webp|gif|heic|heif)$/i;

export function puoAvereAnteprima(file) {
  if (!file) return false;
  if (file.type && TIPI.test(file.type)) return true;
  // Su Android il tipo a volte arriva vuoto: si guarda il nome.
  return !file.type && ESTENSIONI.test(file.name || '');
}

/**
 * Riduce a 256 px il lato lungo, converte in WebP e cifra con la chiave
 * dell'utente. Restituisce { blob, iv } oppure null — mai un'eccezione.
 */
export async function generaAnteprima(file, chiaveUtente) {
  if (!chiaveUtente || !puoAvereAnteprima(file)) return null;
  let bmp = null;
  try {
    // createImageBitmap decodifica fuori dal filo principale e lancia da solo
    // su un file corrotto o su un formato che non conosce: e' il controllo
    // che non serve scrivere a mano.
    bmp = await createImageBitmap(file);
    const scala = Math.min(ANTEPRIMA_LATO / bmp.width, ANTEPRIMA_LATO / bmp.height, 1);
    const w = Math.max(1, Math.round(bmp.width * scala));
    const h = Math.max(1, Math.round(bmp.height * scala));

    let blob;
    if (typeof OffscreenCanvas !== 'undefined') {
      const c = new OffscreenCanvas(w, h);
      c.getContext('2d').drawImage(bmp, 0, 0, w, h);
      blob = await c.convertToBlob({ type: 'image/webp', quality: 0.8 });
    } else {
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      c.getContext('2d').drawImage(bmp, 0, 0, w, h);
      blob = await new Promise((r) => c.toBlob(r, 'image/webp', 0.8));
    }
    if (!blob || !blob.size) return null;

    // L'ANTEPRIMA DEVE CONVENIRE, ALTRIMENTI NON SI FA.
    //
    // Non c'e' una soglia in kilobyte da ricordare: il confronto e' con il
    // file stesso. Se il ridotto pesa quanto o piu' dell'originale — succede
    // con le immagini gia' piccole e con le grafiche piatte, dove il WebP a
    // 256 px non ha niente da guadagnare — allora servirla sarebbe scaricare
    // di piu' per vedere di meno. Si restituisce null: le tre colonne
    // restano nulle e la griglia scarica il file intero, che in quel caso e'
    // la scelta leggera.
    if (blob.size >= file.size) return null;

    // Stessa cifratura del ramo vecchio dei nomi e della didascalia:
    // encryptBuffer restituisce l'iv GIA' in base64, e ri-codificarlo
    // darebbe una stringa vuota. Errore gia' costato una giornata.
    const { ciphertext, iv } = await encryptBuffer(chiaveUtente, await blob.arrayBuffer());
    return { blob: new Blob([ciphertext], { type: 'application/octet-stream' }), iv, misura: blob.size };
  } catch {
    return null;
  } finally {
    try { bmp && bmp.close(); } catch { /* niente */ }
  }
}
